import {useTranslation} from '@aboutyou/i18n';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {
  Button,
  COLORS,
  ContentPopper,
  ContentPopperProps,
  GenericIconName,
  Icon,
  Image,
  RadioButton,
  Text,
  TextInput,
  desktopAndUpLinaria,
  mobileOnly,
  tabletAndUp,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React from 'react';
import {Controller} from 'react-hook-form';
import sc, {css} from 'styled-components';
import {GiftCardRefundFormHeader} from './GiftCardRefundFormHeader';
import GiftCardHelper from './assets/giftcardhelper.svg';
import {
  GiftCardType,
  useGiftCardReturnForm,
} from './hooks/useGiftCardReturnForm';

export const GiftCardRefundForm: React.FC<{
  layoutComponent: ContentPopperProps['LayoutComponent'];
}> = ({layoutComponent}) => {
  const {t} = useTranslation();

  const {open, isOpen, cancel} = useOpenedStateManager('GIFT_CARD_REFUND_FORM');

  const {
    handleSubmit,
    register,
    errors,
    createGiftCardRefund,
    isSuccess,
    isLoading,
    watch,
    control,
  } = useGiftCardReturnForm();

  const handleInfoClick = () => {
    open();
  };

  const type = watch('type');

  if (isSuccess) {
    return (
      <FormWrapper>
        <GiftCardRefundFormHeader />

        <Container>
          <GiftCardSuccessWrapper>
            <Text size="px16" weight="mediumHeavy">
              {t('Your data have been submitted')}
            </Text>
            <Text size="px14" color="Grey1">
              {t(
                'We will process your request as quickly as possible. After we have checked your details, we will transfer the remaining amount of your gift card to your account within 7-10 working days.',
              )}
            </Text>
          </GiftCardSuccessWrapper>
        </Container>
      </FormWrapper>
    );
  }

  return (
    <FormWrapper>
      <GiftCardRefundFormHeader />
      <form
        autoComplete="on"
        onSubmit={handleSubmit(createGiftCardRefund)}
        method="POST"
      >
        <Wrapper>
          <SubheaderWrapper>
            <Text>
              {t(
                'Please enter your gift card information, personal details and your bank data in order for the amount on your gift card to be refunded.',
              )}
            </Text>
          </SubheaderWrapper>
          <Container>
            <GiftCardNumberInputSection>
              <GiftCardHelperRow>
                <Text size="px16" weight="mediumHeavy">
                  {t('Your gift card information')}
                </Text>
                <InfoIcon
                  asset={GenericIconName.InfoFilledGray}
                  size="XS"
                  onClick={handleInfoClick}
                />
              </GiftCardHelperRow>
              <GiftCardTypeContainer>
                <RadioButton
                  {...register('secretNumber')}
                  name="type"
                  label={t('Secret number')}
                  value={GiftCardType.SecretNumber}
                  className="radio-white"
                />

                <RadioButton
                  {...register('serialNumber')}
                  name="type"
                  label={t('Serial number')}
                  value={GiftCardType.SerialNumber}
                  className="radio-white"
                />
              </GiftCardTypeContainer>

              <Controller
                key={type}
                control={control}
                name="giftCardNumber"
                rules={{
                  required: true,
                }}
                render={({field}) => (
                  <FullWidthTextInput
                    {...field}
                    type={
                      type === GiftCardType.SecretNumber ? 'text' : 'number'
                    }
                    placeholder={t('Gift card number')}
                    activeStyle={{
                      backgroundColor: COLORS.Grey4,
                      borderColor: COLORS.Grey4,
                    }}
                    maxLength={type === GiftCardType.SecretNumber ? 10 : 19} // Set maxLength for the input field
                  />
                )}
              />
            </GiftCardNumberInputSection>
          </Container>

          <Container>
            <Text size="px16" weight="mediumHeavy">
              {t('Personal Information')}
            </Text>
            <FullWidthTextInput
              required
              {...register('firstName')}
              placeholder={t('First name')}
              activeStyle={{
                backgroundColor: COLORS.Grey4,
                borderColor: COLORS.Grey4,
              }}
            />
            <FullWidthTextInput
              required
              {...register('lastName')}
              placeholder={t('Last name')}
              activeStyle={{
                backgroundColor: COLORS.Grey4,
                borderColor: COLORS.Grey4,
              }}
            />
            <FullWidthTextInput
              required
              {...register('street')}
              placeholder={t('Street + house no.')}
              activeStyle={{
                backgroundColor: COLORS.Grey4,
                borderColor: COLORS.Grey4,
              }}
            />
            <FullWidthTextInput
              required
              {...register('postalCode')}
              placeholder={t('Postal code')}
              activeStyle={{
                backgroundColor: COLORS.Grey4,
                borderColor: COLORS.Grey4,
              }}
            />
            <FullWidthTextInput
              required
              {...register('city')}
              placeholder={t('City')}
              activeStyle={{
                backgroundColor: COLORS.Grey4,
                borderColor: COLORS.Grey4,
              }}
            />
            <FullWidthTextInput
              required
              {...register('country')}
              placeholder={t('Country')}
              activeStyle={{
                backgroundColor: COLORS.Grey4,
                borderColor: COLORS.Grey4,
              }}
            />
            <FullWidthTextInput
              required
              {...register('email')}
              type="email"
              placeholder={t('Email address')}
              activeStyle={{
                backgroundColor: COLORS.Grey4,
                borderColor: COLORS.Grey4,
              }}
            />
          </Container>
          <Container>
            <Text size="px16" weight="mediumHeavy">
              {t('Bank data')}
            </Text>
            <FullWidthTextInput
              required
              {...register('iban')}
              placeholder={t('IBAN')}
              activeStyle={{
                backgroundColor: COLORS.Grey4,
                borderColor: COLORS.Grey4,
              }}
              errorMessage={errors.iban?.message}
            />
            <IbanDisclaimer size="px14" weight="mediumBold" color="Grey1">
              {t(
                'We use your payment data for refund purposes only. By entering your payment information into the customer account, you agree to their storage and use for future refund purposes. You can modify the information in your customer account at any time.',
              )}
            </IbanDisclaimer>
          </Container>
          <SubmitButtonWrapper>
            <SubmitButton isLoading={isLoading} htmlType="submit">
              {t('Send')}
            </SubmitButton>
          </SubmitButtonWrapper>
        </Wrapper>
      </form>
      <ContentPopper
        LayoutComponent={layoutComponent}
        onClose={cancel}
        slideDirection="left"
        open={isOpen}
        data-testid="giftCardRefundHelper"
        headerContent={
          <Text size="px20" weight="mediumHeavy" textAlignment="center">
            {t('Gift card serial & secret no.')}
          </Text>
        }
        bodyContent={
          <GiftCardHelperWrapper>
            <Text size="px14">
              {t(
                'You will find the secret number (1) or the serial number (2) on the back of your gift card',
              )}
            </Text>
            <GiftCardHelperImage
              ratio="3:2"
              breakpoints={[]}
              src={GiftCardHelper}
            />
          </GiftCardHelperWrapper>
        }
      />
    </FormWrapper>
  );
};

const FormWrapper = sc.div`
  ${tabletAndUp(css`
    padding-top: 2rem;
  `)}
`;

const Wrapper = sc.div`
  display: flex;
  flex-direction: column;
`;

const InfoIcon = linariaStyled(Icon)`
  cursor: pointer;
`;

const SubheaderWrapper = sc.div`
  padding: 1rem 0 2rem;
  ${mobileOnly(css`
    padding: 1.875rem 1rem 0;
    background-color: ${COLORS.White};
  `)}
`;

const Container = sc.div`
  display: flex;
  padding: 1.875rem 1rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
  align-self: stretch;
  background-color: ${COLORS.White};
  border-bottom: 1px solid ${COLORS.Grey3};
  margin-bottom: 0.5rem;
`;

const GiftCardSuccessWrapper = sc.div`
  display: flex;
  flex-direction: column;
  ${mobileOnly(css`
    text-align: center;
    align-items: center;
    justify-content: center;
  `)}

  width: 100%;
  gap: 1.5rem;
`;

const GiftCardHelperRow = sc.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const GiftCardNumberInputSection = sc.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1.5rem;
`;

const GiftCardTypeContainer = sc.div`
  display: flex;
  flex-direction: column;
  padding: 0.75rem 0rem;
  gap: 1.5rem;
`;

const FullWidthTextInput = sc(TextInput)`
  width: 100%;
`;

const IbanDisclaimer = sc(Text)`
  line-height: 1.25rem;
`;

const SubmitButtonWrapper = sc.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  align-self: stretch;
  padding: 2rem 0;
  ${mobileOnly(css`
    padding: 1rem 1rem 1.5rem;

    background-color: ${COLORS.White};
    border-bottom: 1px solid ${COLORS.Grey3};
  `)}
`;

const GiftCardHelperWrapper = sc.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const GiftCardHelperImage = sc(Image)`
  max-width: 100%;
`;

const SubmitButton = sc(Button)`
  ${desktopAndUpLinaria(`
    &&& {
      width: fit-content;
    }
  `)}
`;
